import { useEffect, useState } from 'react';
import { History, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DEFAULT_CHURN_THRESHOLDS,
  getLinkChurnSettings,
  resolveLinkReview,
  subscribeToOpenLinkReviews,
  updateLinkChurnSettings,
  type ChurnRule,
  type ChurnThresholds,
  type LinkReview,
  type LinkReviewAction,
  type LinkReviewType,
} from '../../services/linkReviews';
import {
  getLibrarySongsByKeys,
  getSongLinkHistory,
  type LibraryHistoryEntry,
  type LibrarySong,
} from '../../services/songMatchMemory';
import { signUpErrorMessage } from '../../services/betaSignUp';
import { SongLinkHistoryList } from '../../components/dj/SongLinkHistoryList';
import { canonicalTrackUrl, platformNameOf } from '../../utils/songLinks';
import { useAuth } from '../../context/AuthContext';

const sectionClass = 'bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4';
const headingClass = 'text-lg font-semibold text-gray-900 dark:text-white';
const subheadingClass = 'text-sm font-semibold text-gray-700 dark:text-gray-300';

const GROUPS: Array<{ type: LinkReviewType; heading: string }> = [
  { type: 'flag', heading: 'Flagged by DJs' },
  { type: 'revoked_change', heading: 'Proposed by a revoked DJ' },
  { type: 'churn', heading: 'Churn alerts' },
  { type: 'glitch', heading: 'Wrong song with a catalog ID' },
];

const CHURN_RULE_LABELS: Record<ChurnRule, string> = {
  flip_back: 'Flip-back',
  too_many_changes: 'Too many changes',
  never_settles: 'Never settles',
  report_pile_up: 'Report pile-up',
};

const DELETE_CONFIRM = "Remove this link for this song? DJs won't be able to use or reinstate it.";
const THRESHOLD_MIN = 1;
const THRESHOLD_MAX = 20;

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

function TrackLink({ review, trackId }: { review: LinkReview; trackId: string }) {
  const url = canonicalTrackUrl(review.provider, trackId);
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline break-all">
      <Link2 className="w-3 h-3 inline mr-1" />
      {url}
    </a>
  );
}

interface HistoryDrawerProps {
  review: LinkReview;
  ownerUid: string;
  busy: boolean;
  onDelete: () => void;
}

function HistoryDrawer({ review, ownerUid, busy, onDelete }: HistoryDrawerProps) {
  const [song, setSong] = useState<LibrarySong | null>(null);
  const [entries, setEntries] = useState<LibraryHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([getLibrarySongsByKeys([review.songKey]), getSongLinkHistory(review.songKey)])
      .then(([songs, history]) => {
        if (cancelled) return;
        setSong(songs.get(review.songKey) ?? null);
        setEntries(history);
      })
      .catch((error: unknown) => {
        console.error('Loading the link history failed', error);
        if (!cancelled) setLoadError('Could not load the history.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [review.songKey, review.updatedAt]);

  if (loading) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading history…</p>;
  if (loadError) return <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>;
  if (!song) return <p className="text-sm text-gray-500 dark:text-gray-400">This song is no longer in the library.</p>;

  const canDelete = review.type === 'flag' || review.type === 'churn';
  return (
    <SongLinkHistoryList
      song={song}
      provider={review.provider}
      entries={entries}
      currentDjUid={ownerUid}
      renderActions={(entry) => {
        if (entry.deletedByOwner || !canDelete) return null;
        if (entry.trackId !== review.trackId) {
          return <span className="text-xs text-gray-400 max-w-[9rem]">To delete a different link, flag it first.</span>;
        }
        return (
          <button type="button" className="btn btn-secondary text-xs px-2 py-1" disabled={busy} onClick={onDelete}>
            Delete
          </button>
        );
      }}
    />
  );
}

function ChurnSettingsForm() {
  const [thresholds, setThresholds] = useState<ChurnThresholds>(DEFAULT_CHURN_THRESHOLDS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getLinkChurnSettings()
      .then(setThresholds)
      .catch((error: unknown) => {
        console.error('Loading churn settings failed', error);
        toast.error('Could not load the churn settings. Showing the defaults.');
      })
      .finally(() => setLoading(false));
  }, []);

  const numberFields: Array<{ field: 'tooManyChanges' | 'neverSettlesStreak' | 'reportPileUp'; label: string }> = [
    { field: 'tooManyChanges', label: 'Too many changes: alert after this many changes before a link settles' },
    { field: 'neverSettlesStreak', label: 'Never settles: alert after this many links in a row are replaced before a clean use' },
    { field: 'reportPileUp', label: 'Report pile-up: alert after this many wrong-song reports on one link' },
  ];
  const invalidField = numberFields.find(({ field }) => {
    const value = thresholds[field];
    return !Number.isInteger(value) || value < THRESHOLD_MIN || value > THRESHOLD_MAX;
  });

  const save = async () => {
    if (invalidField) return;
    setSaving(true);
    try {
      setThresholds(await updateLinkChurnSettings(thresholds));
      toast.success('Churn settings saved');
    } catch (error) {
      console.error('Saving churn settings failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <h3 className={subheadingClass}>Churn alert settings</h3>
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={thresholds.flipBackAlerts}
          disabled={loading}
          onChange={(event) => setThresholds({ ...thresholds, flipBackAlerts: event.target.checked })}
        />
        Alert when a link goes back to a track it had before (flip-back)
      </label>
      {numberFields.map(({ field, label }) => (
        <label key={field} className="block text-sm text-gray-700 dark:text-gray-300">
          <span className="block mb-1">{label}</span>
          <input
            type="number"
            min={THRESHOLD_MIN}
            max={THRESHOLD_MAX}
            step={1}
            value={Number.isNaN(thresholds[field]) ? '' : thresholds[field]}
            disabled={loading}
            onChange={(event) => setThresholds({ ...thresholds, [field]: event.target.valueAsNumber })}
            className="input w-24"
          />
        </label>
      ))}
      {invalidField && (
        <p className="text-sm text-red-600 dark:text-red-400">
          Each number must be a whole number from {THRESHOLD_MIN} to {THRESHOLD_MAX}.
        </p>
      )}
      <button type="button" className="btn btn-primary" disabled={loading || saving || Boolean(invalidField)} onClick={save}>
        {saving ? 'Saving…' : 'Save churn settings'}
      </button>
    </div>
  );
}

export function LinkLibrarySection() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<LinkReview[]>([]);
  const [loadError, setLoadError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openHistoryId, setOpenHistoryId] = useState<string | null>(null);

  useEffect(() => subscribeToOpenLinkReviews(
    (open) => { setReviews(open); setLoadError(''); },
    (error) => {
      console.error('Link review subscription failed', error);
      setLoadError('Could not load the link review queue.');
    },
  ), []);

  const act = async (review: LinkReview, action: LinkReviewAction) => {
    if (action === 'delete_link' && !window.confirm(DELETE_CONFIRM)) return;
    setBusyId(review.id);
    try {
      await resolveLinkReview(review.id, action);
      toast.success(action === 'dismiss' ? 'Dismissed' : action === 'delete_link' ? 'Link removed' : 'Change approved');
      if (openHistoryId === review.id) setOpenHistoryId(null);
    } catch (error) {
      console.error(`Link review ${action} failed`, error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className={sectionClass}>
      <h2 className={headingClass}>Link library</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Links DJs flagged, changes from DJs whose link editing is paused, churn alerts and wrong-song reports in Apple modes.
      </p>

      {loadError && (
        <div role="alert" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {loadError}
        </div>
      )}

      {!loadError && reviews.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nothing to review.</p>
      )}

      {GROUPS.map(({ type, heading }) => {
        const group = reviews.filter((review) => review.type === type);
        if (group.length === 0) return null;
        return (
          <div key={type} className="space-y-2">
            <h3 className={subheadingClass}>{heading} ({group.length})</h3>
            <ul className="space-y-3">
              {group.map((review) => {
                const busy = busyId === review.id;
                return (
                  <li key={review.id} data-testid="link-review-card" className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2">
                    <div className="space-y-1 min-w-0">
                      <p className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white break-words">
                          {review.title} — {review.artist}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                          {platformNameOf(review.provider)}
                        </span>
                        {review.churnRule && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                            {CHURN_RULE_LABELS[review.churnRule]}
                          </span>
                        )}
                        {review.count > 1 && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            ×{review.count}
                          </span>
                        )}
                      </p>
                      {review.trackId && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {review.type === 'revoked_change' ? 'Current: ' : ''}<TrackLink review={review} trackId={review.trackId} />
                        </p>
                      )}
                      {review.proposed && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Proposed: <TrackLink review={review} trackId={review.proposed.trackId} />
                        </p>
                      )}
                      {review.detail && <p className="text-sm text-gray-700 dark:text-gray-300">{review.detail}</p>}
                      {review.reason && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">&ldquo;{review.reason}&rdquo;</p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
                        {review.raisedByEmail ?? 'Unknown DJ'} · {formatDate(review.updatedAt)}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {review.type === 'revoked_change' && (
                        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(review, 'approve_change')}>
                          Approve
                        </button>
                      )}
                      {(review.type === 'flag' || review.type === 'churn') && review.trackId && (
                        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => act(review, 'delete_link')}>
                          Delete link
                        </button>
                      )}
                      <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => act(review, 'dismiss')}>
                        Dismiss
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setOpenHistoryId(openHistoryId === review.id ? null : review.id)}
                      >
                        <History className="w-4 h-4 mr-1" />
                        {openHistoryId === review.id ? 'Close history' : 'Open history'}
                      </button>
                    </div>

                    {openHistoryId === review.id && (
                      <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                        <HistoryDrawer
                          review={review}
                          ownerUid={user?.uid ?? ''}
                          busy={busy}
                          onDelete={() => act(review, 'delete_link')}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <ChurnSettingsForm />
      </div>
    </section>
  );
}
